import React, { useState, useEffect, useMemo } from 'react';
import { X, Zap, Check, AlertCircle, Info } from 'react-feather';
import { InlineSelect, InlineCheckbox } from './InlineTable';
import { ToolbarButton, ConfigEmptyState } from './ConfigPages';
import { getAiPositionGlMappingSuggestions } from '../utils/departmentApi';

/**
 * AI position → GL account mapping. Preview the suggestions, override any
 * account inline, tick the ones to keep, apply. Shell = the ConfigPages
 * cp-modal grammar (tokens only, dark mode automatic).
 */
const AiPositionGlMappingModal = ({
  isOpen,
  onClose,
  positions,
  glAccounts,
  existingMappings,
  orgId,
  locationId,
  onApplyMappings,
}) => {
  const [includeAlreadyMapped, setIncludeAlreadyMapped] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [unmappedPositions, setUnmappedPositions] = useState([]);
  const [selectedMappings, setSelectedMappings] = useState(new Set());
  const [metrics, setMetrics] = useState(null);
  const [hasRun, setHasRun] = useState(false);

  // Which positions to send: unmapped only, or everything (remap).
  const positionsToMap = useMemo(() => {
    if (includeAlreadyMapped) return positions;
    const mapped = new Set((existingMappings || []).map((m) => m.positionId));
    return positions.filter((p) => !mapped.has(p.id));
  }, [positions, existingMappings, includeAlreadyMapped]);

  const glAccountOptions = useMemo(() => glAccounts.map((gl) => ({
    value: gl.id, label: `${gl.accountNumber} - ${gl.accountName}`, hint: gl.department || undefined,
  })), [glAccounts]);

  useEffect(() => {
    if (!isOpen) {
      setSuggestions([]); setUnmappedPositions([]); setSelectedMappings(new Set());
      setError(null); setMetrics(null); setHasRun(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const handleRunAiMapping = async () => {
    if (positionsToMap.length === 0) {
      setError('Nothing to map — every position already has a GL account. Tick "Include already-mapped positions" to remap.');
      return;
    }
    setIsLoading(true); setError(null); setSuggestions([]); setUnmappedPositions([]);
    try {
      const result = await getAiPositionGlMappingSuggestions({
        orgId,
        locationId,
        positions: positionsToMap.map((p) => ({ id: p.id, name: p.name, code: p.code, departmentName: p.departmentName })),
        glAccounts: glAccounts.map((gl) => ({ id: gl.id, accountNumber: gl.accountNumber, accountName: gl.accountName, department: gl.department })),
      });
      if (result.success) {
        const withState = (result.mappings || []).map((m) => ({ ...m, selectedGlAccountId: m.glAccountId }));
        setSuggestions(withState);
        setUnmappedPositions(result.unmappedPositions || []);
        setMetrics(result.metrics);
        // Pre-select the confident ones (>= 0.7).
        setSelectedMappings(new Set(withState.filter((m) => m.confidence >= 0.7).map((m) => m.positionId)));
        setHasRun(true);
      } else {
        setError(result.error || 'AI mapping failed. Try again.');
      }
    } catch (err) {
      console.error('Error running AI mapping:', err);
      setError(err.message || 'Could not get AI suggestions. Try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMapping = (positionId) => setSelectedMappings((prev) => { const n = new Set(prev); if (n.has(positionId)) n.delete(positionId); else n.add(positionId); return n; });
  const selectAll = () => setSelectedMappings(new Set(suggestions.map((m) => m.positionId)));
  const deselectAll = () => setSelectedMappings(new Set());
  const handleGlAccountChange = (positionId, glId) => setSuggestions((prev) => prev.map((m) => (m.positionId === positionId ? { ...m, selectedGlAccountId: glId } : m)));

  const applicable = suggestions.filter((m) => selectedMappings.has(m.positionId) && m.selectedGlAccountId != null);
  const handleApplyMappings = () => onApplyMappings(applicable.map((m) => ({ positionId: m.positionId, chartOfAccountId: m.selectedGlAccountId })));

  const confidenceTag = (c) => (c >= 0.8 ? { cls: 'cp-tag cp-ok', label: 'High' } : c >= 0.6 ? { cls: 'cp-tag cp-warn', label: 'Medium' } : { cls: 'cp-tag', label: 'Low' });

  if (!isOpen) return null;

  return (
    <div className="cp-modal-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cp-modal cp-wide" role="dialog" aria-modal="true" aria-label="AI map positions to GL accounts" style={{ display: 'flex', flexDirection: 'column' }}>
        <div className="cp-modal-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ display: 'inline-flex', padding: 8, borderRadius: 10, background: 'var(--lodge-orange-50)', color: 'var(--lodge-orange-500)' }}><Zap size={18} /></span>
            <div>
              <h2>AI map positions</h2>
              <div className="cp-help">Suggests a GL expense account for each position from its name and department; you review before anything is saved.</div>
            </div>
          </div>
          <ToolbarButton small iconOnly variant="ghost" icon={X} onClick={onClose} aria-label="Close" />
        </div>

        <div className="cp-modal-body" style={{ overflow: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {!hasRun && (
            <div className="cp-note">
              <Info size={16} style={{ flexShrink: 0, marginTop: 1 }} />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <InlineCheckbox checked={includeAlreadyMapped} onChange={(e) => setIncludeAlreadyMapped(e.target.checked)} />
                    Include already-mapped positions (remap)
                  </label>
                  <span className="cp-meta">{positionsToMap.length} position{positionsToMap.length !== 1 ? 's' : ''} to map · {glAccounts.length} expense accounts</span>
                </div>
                <div style={{ marginTop: 6 }}>Suggestions follow hotel-industry (USALI) conventions. Nothing is written until you click Apply.</div>
              </div>
            </div>
          )}

          {!hasRun && !isLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0' }}>
              <ToolbarButton variant="ai" icon={Zap} onClick={handleRunAiMapping} disabled={positionsToMap.length === 0}>Generate suggestions</ToolbarButton>
            </div>
          )}

          {isLoading && (
            <ConfigEmptyState title="Analyzing positions…" body="Matching each position to the most likely GL expense account. This takes a few seconds." />
          )}

          {error && (
            <div className="cp-note cp-warn"><AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} /><div>{error}</div></div>
          )}

          {hasRun && !isLoading && suggestions.length > 0 && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span className="cp-meta">
                  {suggestions.length} suggestion{suggestions.length !== 1 ? 's' : ''}
                  {unmappedPositions.length > 0 ? ` · ${unmappedPositions.length} could not be mapped` : ''}
                  {metrics?.estimatedCost != null ? ` · cost $${Number(metrics.estimatedCost).toFixed(4)}` : ''}
                </span>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
                  <ToolbarButton small variant="ghost" onClick={selectAll}>Select all</ToolbarButton>
                  <ToolbarButton small variant="ghost" onClick={deselectAll}>Deselect all</ToolbarButton>
                  <span className="cp-meta">{selectedMappings.size} selected</span>
                </div>
              </div>

              <div className="it-frame">
                <div className="it-scroll" style={{ '--it-max-h': '52vh' }}>
                  <table className="it-tbl">
                    <thead>
                      <tr>
                        <th style={{ width: 44 }}>Use</th>
                        <th>Position</th>
                        <th style={{ minWidth: 320 }}>Suggested GL account</th>
                        <th style={{ width: 110 }}>Confidence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {suggestions.map((s) => {
                        const tag = confidenceTag(s.confidence);
                        const sel = selectedMappings.has(s.positionId);
                        return (
                          <tr key={s.positionId} className={`cp-row${sel ? ' it-row-selected' : ''}`}>
                            <td style={{ padding: '0 12px' }}><InlineCheckbox checked={sel} onChange={() => toggleMapping(s.positionId)} /></td>
                            <td>
                              <div style={{ padding: '6px 12px' }}>
                                <div className="cp-name">{s.positionName}</div>
                                {s.reasoning ? <div className="cp-help" style={{ maxWidth: '52ch' }}>{s.reasoning}</div> : null}
                              </div>
                            </td>
                            <td>
                              <InlineSelect value={s.selectedGlAccountId ?? null} options={glAccountOptions} onChange={(v) => handleGlAccountChange(s.positionId, v ?? null)} placeholder="Choose GL account…" rowActive minWidth={320} ariaLabel="GL account" />
                            </td>
                            <td style={{ padding: '0 12px' }}>
                              <span className={tag.cls} title={`${tag.label} confidence`}>{Math.round(s.confidence * 100)}%</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {unmappedPositions.length > 0 && (
                <div className="cp-note cp-warn">
                  <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{unmappedPositions.length} position{unmappedPositions.length !== 1 ? 's' : ''} could not be mapped</div>
                    <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                      {unmappedPositions.map((up) => <li key={up.positionId}><strong>{up.positionName}</strong>: {up.reason}</li>)}
                    </ul>
                  </div>
                </div>
              )}
            </>
          )}

          {hasRun && !isLoading && suggestions.length === 0 && !error && (
            <ConfigEmptyState
              title="No suggestions"
              body={unmappedPositions.length > 0 ? `${unmappedPositions.length} position${unmappedPositions.length !== 1 ? 's' : ''} could not be matched to an expense account.` : 'The model returned nothing for these positions.'}
            />
          )}
        </div>

        <div className="cp-modal-foot">
          <ToolbarButton variant="ghost" onClick={onClose}>Cancel</ToolbarButton>
          {hasRun && suggestions.length > 0 && (
            <ToolbarButton variant="primary" icon={Check} onClick={handleApplyMappings} disabled={applicable.length === 0}>
              Apply {applicable.length} mapping{applicable.length !== 1 ? 's' : ''}
            </ToolbarButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default AiPositionGlMappingModal;
